import * as React from 'react';
import * as MUI from '@locus-taxy/encore-mui';
import * as Network from '@locus-taxy/encore-ui-network';
import * as Toolkit from '@locus-taxy/encore-ui-toolkit';
import { Box } from '@locus-taxy/encore-mui';
import { createInstance } from '@module-federation/enhanced/runtime';
import ReactDOM from 'react-dom';
import * as ReactRouterDOM from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import { useAuthIntegration } from '../../api/network/auth';
import { useClientId } from '../../hooks/useClientId';
import { CUSTOM_APP_BASE_URL } from '../../config';
import { getTestId } from '@locus-taxy/encore-utils';
import { useRouteContext } from '../../hooks/useRouteContext';
import { TestIdAction } from '../../utils/testIdUtils';

type MapDetailsMfeProps = {
  entityData?: Record<string, unknown>;
  mapId?: string;
  clientId?: string;
  authToken?: string;
};

type MapDetailsRemoteModule = {
  default: React.ComponentType<MapDetailsMfeProps>;
};

const mf = createInstance({
  name: 'host',
  remotes: [
    {
      name: 'map-details',
      entry: `${CUSTOM_APP_BASE_URL}/map-master/mf-manifest.json`,
    },
  ],
  shared: {
    react: {
      version: '19.1.1',
      scope: 'default',
      lib: () => React,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.1.1',
      },
    },
    'react-dom': {
      version: '19.1.1',
      scope: 'default',
      lib: () => ReactDOM,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.1.1',
      },
    },
    'react-router-dom': {
      version: '7.6.3',
      scope: 'default',
      lib: () => ReactRouterDOM,
      shareConfig: {
        singleton: true,
        requiredVersion: '^7.6.3',
      },
    },
    '@locus-taxy/encore-mui': {
      version: '1.0.0-beta.28',
      scope: 'default',
      lib: () => MUI,
      shareConfig: {
        singleton: true,
        requiredVersion: '^1.0.0-beta.28',
      },
    },
    '@locus-taxy/encore-ui-network': {
      version: '3.1.0',
      scope: 'default',
      lib: () => Network,
      shareConfig: {
        singleton: true,
        requiredVersion: '^3.1.0',
      },
    },
    '@locus-taxy/encore-ui-toolkit': {
      version: '5.0.0',
      scope: 'default',
      lib: () => Toolkit,
      shareConfig: {
        singleton: true,
        requiredVersion: '^5.0.0',
      },
    },
  },
});

const MapDetailsLazy = React.lazy(() => {
  return mf.loadRemote('map-details/App').then((value: unknown) => {
    const { default: DefaultExport } = value as MapDetailsRemoteModule;
    const Component = DefaultExport;
    return { default: Component as React.ComponentType<MapDetailsMfeProps> };
  });
});

export const MapDetailsContainer = ({
  entityData,
  mapId,
  hideBorder = false,
}: {
  entityData: Record<string, unknown>;
  mapId: string;
  hideBorder?: boolean;
}) => {
  const { token } = useAuthIntegration();
  const clientId = useClientId();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { entity, page } = useRouteContext();

  // Hide the first two header-like texts inside the MFE (e.g., title and subtitle)
  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const hideHeaders = () => {
      const nodes = root.querySelectorAll('h1, h2, h3, h4, h5, h6, p');
      const visibleTextNodes = Array.from(nodes).filter(n => {
        const text = (n.textContent || '').trim();
        return text.length > 0;
      });
      const toHide = visibleTextNodes.slice(0, 2);
      toHide.forEach(n => {
        const parent = n.parentElement;
        if (parent && parent.contains(n) && parent.childElementCount <= 2) {
          (parent as HTMLElement).style.display = 'none';
        } else {
          (n as HTMLElement).style.display = 'none';
        }
      });
    };

    // Run once after mount
    hideHeaders();

    // Observe for async-loaded content inside the MFE and re-apply
    const observer = new MutationObserver(() => hideHeaders());
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [entityData, mapId]);
  return (
    <Box
      ref={containerRef}
      sx={{
        overflow: 'auto',
        borderRight: hideBorder ? 'none' : '1px solid',
        borderColor: hideBorder ? 'transparent' : 'divider',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
      }}
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'map-details-container',
        entity: entity ?? 'map',
        page: page ?? 'details',
      })}
    >
      <React.Suspense fallback={<LoadingSpinner />}>
        <MapDetailsLazy
          entityData={entityData}
          mapId={mapId}
          clientId={clientId}
          authToken={token ?? undefined}
        />
      </React.Suspense>
    </Box>
  );
};
